import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  createProductionSupportSession,
  pingProductionSupportSession,
  streamProductionSupportMessage,
} from "../api/services/productionSupportAgentService";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";

export interface ProductionSupportChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const WELCOME =
  "State a goal (required), then optionally attach incident files. I search operational errors, APIs, and catalog SOP/runbook/help-guide documents, then recommend next steps. Use Guides & SOPs only to import catalog docs.";

export function useProductionSupportAgentChat() {
  const { activeTenantId } = useAuth();
  const [messages, setMessages] = useState<ProductionSupportChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: WELCOME,
      timestamp: new Date(),
    },
  ]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("production-support", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setContextUsage(null);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    async function restoreSession() {
      if (!activeTenantId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }
      const stored = getStoredAgentSession("production-support", activeTenantId);
      if (!stored?.sessionId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }
      try {
        const valid = await pingProductionSupportSession(stored.sessionId);
        if (cancelled) return;
        if (valid) {
          setSessionId(stored.sessionId);
        } else {
          clearStoredAgentSession("production-support", activeTenantId);
          setSessionId(null);
        }
      } catch {
        if (!cancelled) {
          clearStoredAgentSession("production-support", activeTenantId);
          setSessionId(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const id = await createProductionSupportSession();
    setSessionId(id);
    if (activeTenantId) {
      setStoredAgentSession("production-support", activeTenantId, id);
    }
    return id;
  }, [sessionId, activeTenantId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setError(null);
      setLoading(true);
      const userMsg: ProductionSupportChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: ProductionSupportChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      try {
        const sid = await ensureSession();
        const result = await streamProductionSupportMessage(sid, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            );
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: result.response }
              : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
      } finally {
        setLoading(false);
      }
    },
    [ensureSession],
  );

  return {
    messages,
    loading,
    error,
    contextUsage,
    sessionReady,
    sendMessage,
  };
}
